import { Component, inject, Inject, OnInit, Optional, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import {
  BaseCssProperties,
  FieldAttribute,
  FormFieldConfig,
  ProviderClass,
  UiZoneWidgetAttributeConfig,
  UiZoneWidgetConfig,
  baseCssConfigStyle,
} from '@finxone-platform/shared/sys-config-types';
import { ConfigService } from '../../../../../lib/services/config-service/config-service.service';

import { PageConfigurationProvider } from '@app/finxone-web-frontend/app/Modules/zones/page-configuration.provider';
import { WorkflowFormsService } from '@app/finxone-web-frontend/app/lib/services/workflow-forms/workflow-forms.service';
import { PasswordService } from '@app/finxone-web-frontend/app/lib/services/password-service/password-service.service';
import { FormActionState } from '@app/finxone-web-frontend/app/lib/state/form-submision.state';
import { passwordRulesValidator } from '@app/finxone-web-frontend/app/lib/utils/password-field.utils';
import { Store } from '@ngxs/store';
import Handlebars from 'handlebars';
import { debounceTime, takeUntil } from 'rxjs';
import { ZonePageUrlProvider } from '../../../../zones/zone-url-provider';
import { defaultValidationMessages } from '../../../input-field-widget/default-validation-messages';
import { FormSubmitter } from '../../form-widgets/form-submitter/form-submitter.component';
import { PwnedPasswordService } from './pwned-password.service';
import {
  PARENT_FORM_ROW_GAP,
  RESOLVED_FORM_GROUP_PATH,
} from '@app/finxone-web-frontend/app/lib/utils/di-tokens';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-password-widget',
  templateUrl: './password-widget.component.html',
  styleUrls: ['./password-widget.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class PasswordWidgetComponent extends FormSubmitter implements OnInit {
  public fieldAttributesPassword: FieldAttribute[];
  public widgetConfig: UiZoneWidgetConfig;
  public passwordForm: FormGroup;
  public isError = false;
  public errorMsg = '';
  public borderStyle: baseCssConfigStyle;
  public isPassShowSuccessIcon = false;
  public isPassShowErrorIcon = false;
  public isShowPasswordRule = true;
  public renderedPasswordRulesTemplate = ``;
  public formId: string;
  public childFormKey: string;
  public disableAutocomplete = false;
  public validationMessages = defaultValidationMessages;
  public isPasswordPwned = false;
  private lastCheckedPassword = '';
  public passwordRulesPassed: boolean;
  public passwordRulesValidatorFn = passwordRulesValidator(() => this.passwordRulesPassed);
  public customValidationMessages: { [key: string]: string } | undefined;

  private configService = inject(ConfigService);
  public dataIn = inject<ProviderClass<UiZoneWidgetAttributeConfig>>(ProviderClass);
  protected override store = inject(Store);
  public pageConfig = inject(PageConfigurationProvider);
  private workflowFormsService = inject(WorkflowFormsService);
  private passwordService = inject(PasswordService);
  private zonePageUrlProvider = inject(ZonePageUrlProvider);
  private pwnedPasswordService = inject(PwnedPasswordService);
  public parentFormWidgetRowGap: string | undefined | null = inject(PARENT_FORM_ROW_GAP, { optional: true });
  private resolvedPath = inject(RESOLVED_FORM_GROUP_PATH, { optional: true });

  constructor() {
    super(inject(Store));
    this.assignWidgetConfigData();
  }

  private assignWidgetConfigData(): void {
    if (this.dataIn === undefined || this.dataIn === null) return;
    this.widgetConfig = this.dataIn.widgetConfig;
    this.attri = this.dataIn.attri;
    this.fieldAttributesPassword = this.widgetConfig?.attributes['field_attributes'];
    this.isShowPasswordRule = this.attri?.['isShowPasswordRule'] ?? this.isShowPasswordRule;
    this.disableAutocomplete =
      this.widgetConfig.attributes.widgetProperties?.['disableAutocomplete'] ?? false;
  }

  ngOnInit(): void {
    this.customValidationMessages =
      this.widgetConfig?.attributes.widgetProperties?.inputFieldConfig?.validationMessages;
    this.formId = this.dataIn?.attri?.widgetProperties?.['parentForm'];
    this.childFormKey = this.dataIn?.attri?.widgetProperties?.['childFormKey'];
    this.buildForm();
    this.renderedPasswordRulesTemplate = Handlebars.compile(this.getPasswordRulesText)({});
    this.getCssConfig();
    this.checkPasswordSuccessErrorIconFlag();

    const form = this.passwordForm;
    if (form) {
      this.passwordForm.valueChanges.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(() => {
        const password = this.passwordForm.get('password')?.value || '';
        const confirmPassword = this.passwordForm.get('confirm_password')?.value || '';
        const passwordsMatch = password === confirmPassword && password.length >= 8;
        if (passwordsMatch && password !== this.lastCheckedPassword) {
          this.lastCheckedPassword = password;
          this.pwnedPasswordService
            .checkPassword(password)
            .pipe(takeUntil(this.destroy$))
            .subscribe((isPwned) => {
              this.isPasswordPwned = isPwned;
              this.passwordForm.updateValueAndValidity({ emitEvent: false });
              this.updatePasswordValidity();
            });
        } else if (!passwordsMatch) {
          this.isPasswordPwned = false;
          this.lastCheckedPassword = '';
          this.passwordForm.updateValueAndValidity({ emitEvent: false });
        }

        this.updatePasswordValidity();
      });
    }
  }

  private updatePasswordValidity(): void {
    const passwordControl = this.passwordForm.get('password');
    const confirmPasswordControl = this.passwordForm.get('confirm_password');

    if (!passwordControl || !confirmPasswordControl) {
      this.isError = false;
      return;
    }

    // Touched counts as interacted: a blocked submit only marks nested page forms touched, and
    // dirty-only gating then hid the very error that was blocking the CTA.
    const passwordInteracted = passwordControl.dirty || passwordControl.touched;
    const confirmInteracted = confirmPasswordControl.dirty || confirmPasswordControl.touched;
    const formDirty = passwordInteracted && confirmInteracted;
    const passwordsMatch = this.passwordForm.hasError('noPasswordMatch');
    const anyFieldDirty = passwordInteracted || confirmInteracted;

    this.isError = anyFieldDirty && (passwordsMatch || this.isPasswordPwned);

    if (this.isPasswordPwned && anyFieldDirty) {
      this.errorMsg =
        this.customValidationMessages?.['passwordInBreach'] ||
        'This password has been found in a data breach and is not secure. Please choose a different password.';
    } else if (passwordsMatch && formDirty) {
      this.errorMsg = this.customValidationMessages?.['passwordMatch'] || 'Passwords do not match';
    } else {
      this.errorMsg = '';
    }

    this.checkPasswordSuccessErrorIconFlag();
  }

  private pwnedPasswordFormValidator(): ValidatorFn {
    return (): ValidationErrors | null => {
      const result = this.isPasswordPwned ? { pwnedPassword: true } : null;
      return result;
    };
  }

  private getCssConfig() {
    this.contentStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
      BaseCssProperties.MARGIN,
      BaseCssProperties.PADDING,
    ]);

    this.borderStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
      BaseCssProperties.BORDER,
      BaseCssProperties.BACKGROUND_COLOR,
    ]);
  }

  private buildForm() {
    if (!this.formId) return;

    const formConfig: FormFieldConfig[] = (this.widgetConfig.attributes['field_attributes'] || []).map(
      (field: any) => {
        return {
          id: field.name,
          type: field.type,
          label: field.label || field.name,
          parentForm: this.formId,
          name: field.name,
          validation: {
            required: field.decision_config?.required ?? false,
            customValidator: this.combinedValidator(),
          },
        };
      },
    );
    let formId = this.resolvedPath;
    if (!formId) {
      formId = this.workflowFormsService.generateCustomFormId(this.formId, this.zonePageUrlProvider);
      if (this.childFormKey) {
        formId = `${this.formId}.${this.childFormKey}`;
      }
    }
    this.passwordForm = this.workflowFormsService.getFormPart(formId) ?? new FormGroup({});
    this.passwordForm.addValidators([passwordMatchValidator, this.pwnedPasswordFormValidator()]);
    this.workflowFormsService.addFormControl(this.passwordForm, formConfig);
    const stateValues =
      this.store.selectSnapshot(FormActionState.getFormActionStateWithId(this.formId)) ?? {};

    if (stateValues?.formData) {
      this.patchFormValues(stateValues?.formData);
    }
  }

  public combinedValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const errors: ValidationErrors = {};
      const value = control.value || '';

      // Minimum 8 characters
      if (value?.length < 8) {
        errors['minlength'] = {
          requiredLength: 8,
          actualLength: value?.length,
        };
      }

      // At least one uppercase letter
      if (!/[A-Z]/.test(value)) {
        errors['upperCase'] = true;
      }

      // At least one lowercase letter
      if (!/[a-z]/.test(value)) {
        errors['lowerCase'] = true;
      }

      // At least one number
      if (!/\d/.test(value)) {
        errors['digits'] = true;
      }

      // At least one special character (including extended Unicode symbols)
      if (!/[^\p{L}\p{N}]/u.test(value)) {
        errors['specialChars'] = true;
      }

      // Return collected errors or null if no errors
      return Object.keys(errors).length ? errors : null;
    };
  }

  private checkPasswordSuccessErrorIconFlag() {
    if (this.dataIn && this.widgetConfig) {
      const showSuccessErrorIcon = this.isShowAndErorIcon(this.widgetConfig.attributes['elementsConfig']);
      this.isPassShowErrorIcon = showSuccessErrorIcon.isShowErrorIcon;
      this.isPassShowSuccessIcon = showSuccessErrorIcon.isShowSuccessIcon;
    }
  }

  public patchFormValues(formData: Record<string, string>) {
    this.passwordForm.patchValue({
      password: formData?.['password'],
      confirm_password: formData?.['confirm_password'],
    });
    this.updatePasswordValidity();
  }

  public changePassType(field: FieldAttribute) {
    field.type = field.type == 'text' ? 'password' : 'text';
  }

  public get getPasswordRulesText(): string {
    if (this.attri?.['passwordRules'] != null) return this.attri?.['passwordRules'];
    else return ``;
  }

  public allPasswordRulesPassed(passed: boolean): void {
    this.passwordRulesPassed = passed;
  }

  public getRequiredMessage(field: any): string {
    if (field?.name === 'password') {
      return (
        this.customValidationMessages?.['passwordRequired'] || this.validationMessages.required(field?.label)
      );
    }
    if (field?.name === 'confirm_password') {
      return (
        this.customValidationMessages?.['confirmPasswordRequired'] ||
        this.validationMessages.required(field?.label)
      );
    }
    return this.validationMessages.required(field?.label);
  }
}

export const passwordMatchValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const password = control.get('password')?.value;
  const confirmPassword = control.get('confirm_password')?.value;

  if (!password || !confirmPassword) {
    return null; // don't validate if one is empty
  }

  return password === confirmPassword ? null : { noPasswordMatch: true };
};
